/**
 * B2B / business VPN and Zero Trust Network Access (ZTNA) competitor data.
 * Figures reflect publicly published pricing as of mid-2026. This market
 * reprices and restructures tiers often - spot-check current rates on each
 * vendor's own pricing page before relying on this table for a buying
 * decision. Update values here; the UI reads directly from this file.
 */

export const B2B_COMPETITORS = [
  {
    name: "NordLayer",
    category: "Business VPN",
    price: "~$7-$14/user/mo",
    priceNote: "Billed annually. Tiers named Lite/Core/Premium.",
    minimum: "5-seat minimum on every paid plan",
    protocol: "OpenVPN / IKEv2",
    note: "NordVPN's consumer VPN brand extended into a B2B upsell, not a business-first product.",
  },
  {
    name: "GoodAccess",
    category: "Business VPN / ZTNA",
    price: "~$7-$11/user/mo",
    priceNote: "Billed annually, plus ~$49/mo per extra dedicated gateway.",
    minimum: "5-seat minimum (Essential), 50-seat minimum (Enterprise)",
    protocol: "OpenVPN / IKEv2 IPsec",
    note: "Openly targets 50-5,000 employee companies. A sub-20-person team is a marginal customer by their own stated focus.",
  },
  {
    name: "Twingate",
    category: "ZTNA (not a VPN)",
    price: "Free up to 5 users, then ~$10/user/mo",
    priceNote: "Enterprise tier is contact-sales only.",
    minimum: "None on the free tier",
    protocol: "Zero Trust connectors (split-tunnel, per-resource)",
    note: "A genuinely different product: app-by-app access, not full network presence. Deploy a connector per subnet or resource.",
  },
  {
    name: "Cloudflare Access",
    category: "ZTNA (not a VPN)",
    price: "Free up to 50 seats, then ~$7/user/mo",
    priceNote: "Seats are consumed by any login/device enrollment and don't auto-release.",
    minimum: "None, but assumes you already run an identity provider",
    protocol: "WARP client + Access policies",
    note: "Free doesn't mean turnkey - real setup lift if you don't already have IT/identity infrastructure in place.",
  },
];

export const B2B_COMPARISON_TABLE = [
  {
    feature: "Network model",
    sacvpn: "Full-tunnel WireGuard VPN",
    competitors: "Mixed: full VPN (NordLayer, GoodAccess) or ZTNA app broker (Twingate, Cloudflare Access)",
    advantage: "Full network presence for remote desktops, printers, and legacy on-prem systems - not just SaaS apps",
  },
  {
    feature: "Seat / user minimum",
    sacvpn: "No per-seat minimum",
    competitors: "5-seat minimum (NordLayer, GoodAccess Essential); 50-seat minimum (GoodAccess Enterprise)",
    advantage: "Priced for the team you actually have, not a headcount floor",
  },
  {
    feature: "Published pricing",
    sacvpn: "Real prices on our pricing page",
    competitors: "Top tiers gated behind “contact sales” (NordLayer, GoodAccess, Twingate)",
    advantage: "See the real cost before you talk to anyone",
  },
  {
    feature: "Protocol",
    sacvpn: "WireGuard only",
    competitors: "GoodAccess and NordLayer still lean on OpenVPN/IKEv2 for some tiers",
    advantage: "Modern, faster, and simpler to reason about",
  },
  {
    feature: "US server footprint",
    sacvpn: "3 real US nodes: Texas, Virginia, Dallas",
    competitors: "Marketing around large global node counts you have to trust blindly",
    advantage: "A footprint you can verify, not a number to take on faith",
  },
  {
    feature: "Support",
    sacvpn: "Direct email support from the people who build SACVPN",
    competitors: "Self-serve docs at scale (Cloudflare); tiered or paid support elsewhere",
    advantage: "You're a customer, not a ticket number in someone else's queue",
  },
];

export const SACVPN_B2B_ADVANTAGES = [
  "No 5-seat (or 50-seat) minimum to buy in",
  "Real prices on our pricing page, no “contact sales” wall",
  "Full-tunnel WireGuard VPN, not an app-by-app access broker",
  "3 real US nodes - Texas, Virginia, Dallas - and we tell you exactly where",
  "Direct email support from the people who built it",
  "Veteran-owned, US-based company",
];
